import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { ProviderSummaryTypes } from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { RatingText } from '../../../../../common/DataCard/DataCard.style';
import { getPlanSelectorVariables } from '../../../../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../hooks/useLagoon';
import { ProviderType } from '../../../../../models/ProviderDetails';
import { useChipStore } from '../../../../../store/useChipStore';
import { ChipState } from '../../../../../store/useChipStore/chipStore';
import { useVirtualCareStore } from '../../../../../store/useVirtualCareStore';
import { returnSuppressFlag } from '../../../../../utils/featureSuppress';
import { getFormattedPhoneNumber } from '../../../../../utils/phone.utils';
import {
  getNameAttrs,
  parseProviderName,
} from '../../../../../utils/providerNames.utils';
import { shouldSuppressAddressAndMiles } from '../../../../../utils/providerSearch.utils';
import { isVirtualVisitOnlyAddress } from '../../../../../utils/virtualVisitOnly';
import {
  PrintViewProviderDetailsNameText,
  PrintViewProviderDetailsXSText,
} from './PrintView.style';
import { getStarsRating } from './utils';

type Props = {
  provider: {
    numberOfReviews?: string;
    acceptingNewPatients: boolean;
    providerName: string;
    providerId: string;
    primaryDegrees: string[];
    speciality: string;
    organizationType: string;
    providerType?: string;
    networkStatus: string;
    distance: string;
    isPreferredLab?: boolean;
    isFreeStandingFacility?: boolean;
    ddpCode?: string;
    healthGradeRating?: string;
    isTieredProvider: boolean;
    virtualCareOffered?: boolean;
    languagesSpoken: string[];
    virtualIndicator: string;
    website: string;
    address: {
      postalCode: string;
      district: string;
      line: string[];
      state: string;
      city: string;
      locationName: string;
    };
    firstName?: string;
    middleName?: string;
    lastName?: string;
    costIndicator?: string;
    phones: {
      phone: string[];
    };
  };
};

export const ProviderDemographics = ({ provider }: Props) => {
  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
    }))
  );
  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );
  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const allConfigs = useLagoon('config')();
  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const { planToken } = getPlanSelectorVariables();
  const [enableVirtualCareEpic] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
  ]);
  const suppressAddressAndMiles = shouldSuppressAddressAndMiles(
    enableVirtualCareEpic,
    isVirtualCareUnetRailroadSuppressed,
    provider?.virtualIndicator || '',
    chipValue,
    eligibilities[planToken],
    allConfigs
  );
  const isVirtualVisitOnlyAddressLine = (
    addressLine: string,
    suppressVirtualCareQuickCare: boolean
  ) =>
    suppressVirtualCareQuickCare &&
    isVirtualVisitOnlyAddress(chipValue, addressLine);
  const shouldShowVirtualVisitAddress =
    isVirtualVisitOnlyAddressLine(
      provider?.address?.line?.[0],
      isVirtualCareQuickCareSuppressed
    ) || suppressAddressAndMiles;

  const { t } = useTranslation();
  const suppressRatings: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );
  const { healthGradeRating = '' } = provider;
  const nameAttrs = getNameAttrs(provider);
  const parsedHealthGradeRating = parseFloat(
    parseFloat(healthGradeRating).toFixed(1)
  );

  return (
    <Flex
      css={{
        'abyss-flex-root': {
          gap: '2px',
        },
      }}
      direction="column"
    >
      <PrintViewProviderDetailsNameText color="#333333">
        {parseProviderName(
          provider.providerName,
          provider.providerType as ProviderType,
          provider.primaryDegrees?.[0],
          nameAttrs
        )}
      </PrintViewProviderDetailsNameText>
      <PrintViewProviderDetailsXSText color="#333333">
        {provider?.speciality || provider?.organizationType}
      </PrintViewProviderDetailsXSText>
      {!suppressRatings &&
        provider?.providerType === ProviderSummaryTypes.PRACTITIONER && (
          <Flex>
            <Layout.Group space="">
              {getStarsRating(parsedHealthGradeRating)}
              {parsedHealthGradeRating ? (
                <RatingText className="text" fontSize="xs">
                  {provider?.numberOfReviews
                    ? `${provider?.numberOfReviews} Reviews`
                    : parsedHealthGradeRating}
                </RatingText>
              ) : null}
            </Layout.Group>
          </Flex>
        )}
      <Flex>
        <PrintViewProviderDetailsXSText color="#333333">
          {provider.languagesSpoken?.join(', ')}
        </PrintViewProviderDetailsXSText>
      </Flex>
      <Flex>
        {!shouldShowVirtualVisitAddress &&
        provider?.address?.city &&
        provider?.address?.state ? (
          <PrintViewProviderDetailsXSText color="#333333">
            {`${provider?.address?.line?.[0]}, ${provider?.address?.city}, ${
              provider?.address?.state
            } • ${parseFloat(provider?.distance).toFixed(1)} ${t(
              'SHARE_PDF.Miles away'
            )}`}
          </PrintViewProviderDetailsXSText>
        ) : (
          <PrintViewProviderDetailsXSText color="#333333">
            {provider?.website}
          </PrintViewProviderDetailsXSText>
        )}
      </Flex>
      <Flex>
        <PrintViewProviderDetailsXSText color="#333333">
          {getFormattedPhoneNumber(provider.phones?.phone?.[0])}
        </PrintViewProviderDetailsXSText>
      </Flex>
    </Flex>
  );
};
